/**
 * Ojasa Mirai Unified Messaging System
 * Single source of truth for all marketing content across pages.
 * All pages pull text, CTAs, stats, FAQs, and tone from here.
 */

export const coreMessaging = {
  brand: "Ojasa Mirai",
  tagline: "Empowering the Next Generation of AI Developers",
  heroHook: "Launch Your GenAI Career — Build Real AI Apps in 24 Weeks",
  heroSub:
    "Master Fullstack + GenAI with mentor-led live sessions, 10+ real projects, and career guidance.",
  programSummary:
    "A proven, structured path from Python basics to building production-ready GenAI applications.",

  ctaFlow: {
    webinar: "Join Free Webinar",
    enroll: "Enroll Now",
    advisor: "Talk to an Advisor",
    demo: "Book a Demo",
    curriculum: "View Curriculum",
  },

  trustElements: [
    { icon: "FaUsers", text:  `300+ Learners` },
    { icon: "FaStar", text: "4.8★ Student Rating" },
    { icon: "FaBriefcase", text: "85% Placement Success" },
    { icon: "FaAward", text: "14-Day Money-Back Guarantee" },
  ],

  outcomeStats: {
    duration: "24 Weeks",
    projects: "10+ Hands-on Projects",
    rating: "4.8 / 5",
    placement: "85% Interview-Ready",
  },

  highlights: [
    "Live mentor-led sessions",
    "Structured learning from Python to GenAI",
    "Career mentorship and resume reviews",
    "Build deployable AI projects",
    "Verified certification",
  ],
};

export const personaMessaging = {
  beginner: {
    title: "Perfect for Students & Career Switchers",
    subtext:
      "Start from scratch, master GenAI and Fullstack skills, and graduate job-ready.",
    outcomes: [
      "Master Python, ML, and GenAI fundamentals",
      "Build 10+ portfolio projects",
      "Land an AI developer role with confidence",
    ],
  },

  professional: {
    title: "Designed for Developers & Tech Professionals",
    subtext:
      "Accelerate your career with advanced LLMs, Vector DBs, and RAG systems.",
    outcomes: [
      "Build and deploy production-grade AI services",
      "Upskill with real-world GenAI projects",
      "Move into high-demand AI engineering roles",
    ],
  },
};

export const componentMessaging = {
  valueProposition: {
    heading: "Why Choose Our GenAI Program?",
    subtitle:
      "Everything you need to master AI — delivered through live mentorship and hands-on projects.",
    points: [
      {
        title: "Structured Learning Path",
        description:
          "Start from Python basics, advance through ML, NLP, and GenAI — never feel lost.",
      },
      {
        title: "Hands-On Projects",
        description:
          "Work on 10+ portfolio-ready GenAI apps that demonstrate real-world capability.",
      },
      {
        title: "Career Acceleration",
        description:
          "Interview prep, resume guidance, and direct support to land your AI role.",
      },
    ],
  },

  webinar: {
    title: "Free GenAI Career Webinar",
    subtitle:
      "Discover how to break into AI — even with zero experience.",
    keyPoints: [
      "Understand the 2026 AI job landscape",
      "Learn the top 3 tools every GenAI engineer uses",
      "Build your first AI project in minutes",
      "Explore career paths and salaries in GenAI",
      "Ask live questions to industry mentors",
    ],
    cta: "Reserve My Spot",
  },



contact: {
  title: "Let’s Talk About Your AI Journey",
  subtitle: "Questions? We're here to help.",
  form: {
    title: "Send us a message",
    responseTime: "We'll respond within 24 hours",
        submitLabel: "Schedule 1-on-1 Consultation",
interestOptions: [
      { value: "beginner", label: "Beginner Track" },
      { value: "experienced", label: "Experienced Track" },
      { value: "corporate", label: "Corporate Training" },
      { value: "consulting", label: "Consulting" },
      { value: "general", label: "General Query" },
    ],
  },
  quickLinks: {
    title: "Looking for something specific?",
    links: [
      {
        title: "Webinar",
        description: "Register for webinar",
        to: "/webinar",
      },
      {
        title: "View Curriculum",
        description: "Get course details",
        to: "/beginner-track",
      },
      {
        title: "Join Discord",
        description: "Community support",
        to: "https://discord.gg/yuq4GU9Xab",
      },
    ],
  },
},


  enroll: {
    title: "Your Journey to a GenAI Career Starts Here",
    subtitle:
      "Enroll now and gain lifetime access to learning, mentorship, and community support.",
    benefits: [
      "Hands-on learning with live mentors",
      "Build deployable AI apps and APIs",
      "Lifetime access to materials and community",
    ],
    guarantee: "100% Risk-Free: 14-Day Refund Guarantee",
  },

  testimonials: [
    {
      name: "Rahul M",
      role: "AI Developer @ FinTech",
      text: "This course pushed me to build deployable systems. Mentor code reviews were the difference.",
    },
    {
      name: "Priya S",
      role: "ML Engineer @ Tech Startup",
      text: "From zero to AI developer in months — real projects made all the difference.",
    },
    {
      name: "Yashwant",
      role: "AI Developer",
      text: "The structure and clarity gave me confidence for technical interviews.",
    },
  ],

  faq: [
    {
      q: "I have no coding background. Can I join?",
      a: "Absolutely! Our beginner track starts from scratch and builds your confidence step-by-step.",
    },
    {
      q: "Do you provide placement assistance?",
      a: "Yes, you’ll get resume support, interview prep, and career mentoring until you land a role.",
    },
    {
      q: "What if I don’t like the course?",
      a: "No worries — we offer a 14-day full refund. Try the first 2 weeks risk-free.",
    },
  ],

  footer: {
    quickLinks: [
      { text: "Beginner Track", href: "/beginner-track" },
      { text: "Experienced Track", href: "/experienced-track" },
      { text: "Free Webinar", href: "/webinar" },
      { text: "Why Us", href: "/why-us" },
      { text: "Contact", href: "/contact" },
    ],
    social: {
      linkedin: "https://www.linkedin.com/in/ojasa-mirai-llp-aa3968388/",
      youtube: "https://www.youtube.com/@OjasaMirai",
      instagram: "https://www.instagram.com/ojasa_mirai_llp/",
      discord: "https://discord.gg/ojasamirai",
    },
    legal: {
      privacy: "/privacy-policy",
      terms: "/terms-of-service",
    },
    trustBadges: [
      "14-Day Refund Guarantee",
      "Verified Certification",
      "Lifetime Access",
    ],
  },
};

export const benefitLanguage = {
  featuresToBenefits: {
    "Live Mentorship": "Learn directly from experts — not recorded videos.",
    "Projects": "Prove your skills with real, portfolio-ready AI apps.",
    "Certification": "Show recruiters a verified proof of your expertise.",
    "Career Support": "Land your first AI role with guided prep and mentoring.",
  },
};



export default {
  coreMessaging,
  personaMessaging,
  componentMessaging,
  benefitLanguage,
};
